import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { AuthShell } from './AuthShell';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';

/**
 * Reached via the link Supabase sends after `resetPasswordForEmail`. By the
 * time this page mounts, `detectSessionInUrl` has exchanged the recovery
 * tokens and `supabase.auth.updateUser({ password })` is allowed.
 */
export function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    if (password.length < 8) {
      setError(t('auth.reset_password.min_length'));
      return;
    }
    if (password !== confirm) {
      setError(t('auth.reset_password.mismatch'));
      return;
    }

    setSubmitting(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.errors.unknown'));
      setSubmitting(false);
    }
  };

  return (
    <AuthShell title={t('auth.reset_password.title')} subtitle={t('auth.reset_password.subtitle')}>
      {error && <ErrorAlert message={error} />}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          label={t('auth.reset_password.password')}
          name="password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Input
          label={t('auth.reset_password.password_confirm')}
          name="password_confirm"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        <Button type="submit" size="full" disabled={submitting}>
          {submitting ? t('auth.reset_password.submitting') : t('auth.reset_password.submit')}
        </Button>
      </form>
    </AuthShell>
  );
}
